import React from 'react';
import { Box, Typography, Container, Grid, Button, Chip, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import BalanceIcon from '@mui/icons-material/Balance';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import LaptopMacIcon from '@mui/icons-material/LaptopMac';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import Diversity1Icon from '@mui/icons-material/Diversity1';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';


import retreatImg from '../images/bali_retreat.jpg';
import hackathonImg from '../images/hackathon.jpg';
import remoteImg from '../images/remote_work.jpg';

const jobs = [
    {
        title: "Senior AI Research Scientist",
        type: "Full-Time",
        location: "Remote",
        department: "R&D",
        icon: <TipsAndUpdatesIcon />,
        color: "#673AB7",
        desc: "Lead fundamental research in Large Language Models and Generative AI. Drive the creation of proprietary algorithms that define the next generation of cognitive computing."
    },
    {
        title: "Principal DevOps Architect",
        type: "Full-Time",
        location: "Hybrid (Chennai)",
        department: "Infrastructure",
        icon: <RocketLaunchIcon />,
        color: "#E91E63",
        desc: "Design self-healing, multi-cloud infrastructures using Kubernetes and Terraform. Ensure 99.999% availability for mission-critical enterprise platforms."
    },
    {
        title: "Blockchain Solutions Architect",
        type: "Contract",
        location: "Remote",
        department: "Blockchain",
        icon: <AttachMoneyIcon />,
        color: "#FF9800",
        desc: "Architect secure, decentralized financial protocols. Expertise in Zero-Knowledge Proofs, Solidity, and Layer-2 scaling solutions required."
    },
    {
        title: "Lead Experience Designer",
        type: "Full-Time",
        location: "On-Site",
        department: "Design",
        icon: <LaptopMacIcon />,
        color: "#00BCD4",
        desc: "Define the visual language of our enterprise suite. Translate complex data ecosystems into intuitive, human-centric user experiences."
    },
    {
        title: "Technical Lead - Full Stack",
        type: "Full-Time",
        location: "Remote",
        department: "Engineering",
        icon: <GroupsIcon />,
        color: "#4CAF50",
        desc: "Orchestrate the development of scalable microservices. Mentor engineering squads and enforce world-class code quality standards."
    },
    {
        title: "Global Growth Strategist",
        type: "Full-Time",
        location: "Hybrid",
        department: "Marketing",
        icon: <EmojiEventsIcon />,
        color: "#F44336",
        desc: "Lead data-driven market penetration strategies. optimize  customer acquisition funnels and drive brand authority in new territories."
    }
];

const benefits = [
    { title: "Top-Tier Compensation", icon: <AttachMoneyIcon />, color: "#FFE0B2", textColor: "#E65100", desc: "Market-leading salary packages, performance bonuses, and substantial equity grants." },
    { title: "Comprehensive Health", icon: <HealthAndSafetyIcon />, color: "#FFCDD2", textColor: "#C62828", desc: "Full medical, dental, and vision coverage for you and your dependents, plus wellness stipends." },
    { title: "Remote-First & Flexible", icon: <LaptopMacIcon />, color: "#BBDEFB", textColor: "#1565C0", desc: "Work from anywhere in the world. We prioritize outcomes and impact over specific working hours." },
    { title: "Global Learning Fund", icon: <SchoolIcon />, color: "#E1BEE7", textColor: "#6A1B9A", desc: "Generous budget for conferences, workshops, and certifications to fuel your continuous growth." },
    { title: "Holistic Well-being", icon: <BalanceIcon />, color: "#C8E6C9", textColor: "#2E7D32", desc: "Mandatory disconnect days, mental health support, and flexible time-off policies." },
    { title: "Diverse Community", icon: <Diversity1Icon />, color: "#F0F4C3", textColor: "#827717", desc: "Join a truly global team that celebrates capability, perspective, and shared ambition." },
];

const processSteps = [
    { label: "Apply", icon: <LaptopMacIcon />, desc: "Submit your resume & portfolio." },
    { label: "Screening", icon: <HourglassEmptyIcon />, desc: "Initial chat with HR." },
    { label: "Technical Round", icon: <TipsAndUpdatesIcon />, desc: "Deep dive into skills." },
    { label: "Culture Fit", icon: <GroupsIcon />, desc: "Meet the team." },
    { label: "Offer", icon: <CheckCircleIcon />, desc: "Welcome aboard!" },
];

const SectionHeader = ({ title, subtitle, center = false, light = false }) => (
    <Box mb={8} textAlign={center ? "center" : "left"}>
        <Typography variant="overline" sx={{ color: light ? '#A5D6A7' : '#1A9E7F', letterSpacing: '0.2em', fontWeight: 700 }}>
            {subtitle}
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 800, mt: 1, background: light ? 'linear-gradient(45deg, #fff, #A5D6A7)' : 'linear-gradient(45deg, #034522, #1A9E7F)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {title}
        </Typography>
    </Box>
);

const values = [
    { title: "Innovation First", icon: <AutoAwesomeIcon />, color: "#6200EA", desc: "We don't just follow trends; we set them. We build the technologies that others haven't even imagined yet." },
    { title: "Radical Transparency", icon: <GroupsIcon />, color: "#D50000", desc: "Trust is our currency. We share our strategies, wins, and challenges openly across the entire organization." },
    { title: "Client Obsession", icon: <EmojiEventsIcon />, color: "#FFD600", desc: "We exist to solve our clients' hardest problems. We measure our success solely by their outcomes." },
    { title: "Sustainable Scale", icon: <RocketLaunchIcon />, color: "#00C853", desc: "We build resilient systems designed for the long term, balancing rapid growth with technical stability." },
];

const faqs = [
    { q: "What is the interview process like?", a: "Our process typically involves an initial screening, a technical assessment, and a culture fit interview. We aim to make it smooth and transparent." },
    { q: "Do you offer remote work?", a: "Yes! We are a remote-first company with hubs in major cities for those who prefer an office environment." },
    { q: "What about professional development?", a: "We offer a generous annual stipend for courses, conferences, and certifications to help you grow your skills." },
];

function Careers() {

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA', pt: 0 }}>
            
            {/* 🔹 HERO SECTION - MORE COLORFUL */}
            <Box sx={{ position: 'relative', background: 'linear-gradient(135deg, #002613 0%, #004d40 100%)', color: 'white', pt: 20, pb: 25, overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: '-60%', right: '-20%', width: '1200px', height: '1200px', background: 'radial-gradient(circle, rgba(29, 233, 182, 0.15) 0%, transparent 60%)', borderRadius: '50%' }} />
                <Box sx={{ position: 'absolute', bottom: '-40%', left: '-10%', width: '800px', height: '800px', background: 'radial-gradient(circle, rgba(105, 240, 174, 0.1) 0%, transparent 50%)', borderRadius: '50%' }} />
                
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>

                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        <Typography variant="overline" sx={{ letterSpacing: '0.4em', color: '#69F0AE', fontWeight: 700 }}>JOIN THE REVOLUTION</Typography>
                        <Typography variant="h1" sx={{ fontWeight: 900, fontSize: { xs: '3rem', md: '6rem' }, lineHeight: 1, mb: 4, mt: 2, background: 'linear-gradient(to right, #ffffff, #B9F6CA)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                            Create the <br /> 
                            <span style={{ color: '#1DE9B6', textShadow: '0 0 40px rgba(29, 233, 182, 0.5)' }}>Impossible.</span>
                        </Typography>
                        <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: '800px', mx: 'auto', fontWeight: 300, mb: 6, lineHeight: 1.8, fontSize: '1.3rem' }}>
                            We are not just a tech company. We are a collective of dreamers, builders, and optimists designing the future of intelligence.
                        </Typography>
                        <Button size="large" variant="contained" sx={{ bgcolor: '#1DE9B6', color: '#002613', borderRadius: 50, px: 6, py: 2, fontSize: '1.1rem', fontWeight: 800, boxShadow: '0 0 20px rgba(29, 233, 182, 0.4)', '&:hover': { bgcolor: '#00BFA5', transform: 'scale(1.05)' }, transition: 'all 0.3s' }} onClick={() => document.getElementById('open-positions').scrollIntoView({ behavior: 'smooth' })}>
                            Explore Opportunities
                        </Button>
                    </motion.div>
                </Container>
            </Box>

            {/* 🔹 CORE VALUES - COLORFUL GRID */}
            <Box sx={{ py: 15, bgcolor: '#fff', position: 'relative', mt: -10, zIndex: 2 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={8}>
                        <Grid item xs={12} md={4}>
                             <Box sx={{ position: 'sticky', top: 100 }}>
                                 <SectionHeader title="Our DNA" subtitle="What Drives Us" />
                                 <Typography variant="h6" sx={{ color: '#333', mb: 4, lineHeight: 1.8 }}>
                                     We believe that great work comes from a place of passion, curiosity, and fearless experimentation. These value aren't rules—they're our heartbeat.
                                 </Typography>
                                 <Button variant="outlined" sx={{ color: '#034522', borderColor: '#034522', borderRadius: 50, textTransform: 'none', px: 4, py: 1 }}>
                                     Read Our Story
                                 </Button>
                             </Box>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Grid container spacing={4}>
                                {values.map((v, i) => (
                                    <Grid item xs={12} sm={6} key={i}>
                                         <motion.div whileHover={{ y: -5 }}>
                                             <Paper elevation={0} sx={{ p: 4, bgcolor: '#ffffff', borderRadius: 6, height: '100%', border: '1px solid #f0f0f0', boxShadow: '0 10px 40px rgba(0,0,0,0.03)', position: 'relative', overflow: 'hidden' }}>
                                                 <Box sx={{ position: 'absolute', top: 0, left: 0, width: 6, height: '100%', bgcolor: v.color }} />
                                                 <Box sx={{ width: 50, height: 50, borderRadius: '50%', bgcolor: `${v.color}20`, color: v.color, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                                                     {v.icon}
                                                 </Box>
                                                 <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: '#1a1a1a' }}>{v.title}</Typography>
                                                 <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.7 }}>{v.desc}</Typography>
                                             </Paper>
                                         </motion.div>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* 🔹 BENEFITS - COLORFUL CARDS */}
            <Box sx={{ py: 15, bgcolor: '#FAFAFA' }}>
                <Container maxWidth="lg">
                    <SectionHeader title="Perks & Benefits" subtitle="We Take Care of You" center />
                    <Grid container spacing={4}>
                        {benefits.map((b, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <motion.div whileHover={{ y: -10, rotate: 1 }} transition={{ type: 'spring', stiffness: 300 }}>
                                    <Paper elevation={0} sx={{ p: 4, height: '100%', borderRadius: 6, bgcolor: b.color, color: b.textColor, transition: 'all 0.3s', '&:hover': { boxShadow: '0 20px 40px rgba(0,0,0,0.1)' } }}>
                                        <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.6)', color: b.textColor, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, boxShadow: '0 5px 15px rgba(0,0,0,0.05)' }}>
                                            {b.icon}
                                        </Box>
                                        <Typography variant="h6" fontWeight={800} gutterBottom>{b.title}</Typography>
                                        <Typography variant="body2" sx={{ lineHeight: 1.6, opacity: 0.9 }}>{b.desc}</Typography>
                                    </Paper>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* 🔹 CULTURE CODE / MANIFESTO (From LifeAtGAT) */}
            <Box sx={{ py: 15, bgcolor: '#FAFAFA' }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={10}>
                        <Typography variant="overline" sx={{ letterSpacing: 4, fontWeight: 700, color: '#1a1a1a' }}>Culture Code</Typography>
                        <Typography variant="h3" fontWeight={800} color="#034522" mt={1}>Why We Are Different</Typography>
                        <Typography variant="h6" color="text.secondary" maxWidth="700px" mx="auto" mt={2}>
                            We don't follow the herd. We set the pace. Here is what defines us.
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {[
                            { icon: <EmojiEventsIcon fontSize="large" />, title: "Relentless Excellence", desc: "Good isn't enough. We strive for mastery in every line of code.", color: "#FFD700" },
                            { icon: <GroupsIcon fontSize="large" />, title: "Radical Innovation", desc: "We disrupt the status quo. If it's safe, it's not for us.", color: "#FF9A15" },
                            { icon: <Diversity1Icon fontSize="large" />, title: "One Team, One Dream", desc: "We win together. Collaboration is our secret weapon.", color: "#00C853" },
                            { icon: <RocketLaunchIcon fontSize="large" />, title: "Growth Mindset", desc: "We fail fast, learn faster, and never stop evolving.", color: "#2979FF" }
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <motion.div whileHover={{ y: -10 }} transition={{ duration: 0.3 }}>
                                    <Paper elevation={0} sx={{ p: 5, borderRadius: 6, bgcolor: 'white', border: '1px solid #eee', textAlign: 'center', height: '100%', position: 'relative', overflow: 'hidden' }}>
                                        <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '6px', bgcolor: item.color }} />
                                        <Box sx={{ bgcolor: `${item.color}20`, width: 80, height: 80, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3, color: item.color }}>
                                            {item.icon}
                                        </Box>
                                        <Typography variant="h4" fontWeight={800} gutterBottom>{item.title}</Typography>
                                        <Typography variant="body1" color="text.secondary" lineHeight={1.8}>{item.desc}</Typography>
                                    </Paper>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* 🔹 EXTENDED PERKS (From LifeAtGAT) */}
            <Box sx={{ py: 15, bgcolor: '#002613', color: 'white' }}>
                 <Container maxWidth="lg">
                     <Typography variant="h3" fontWeight={800} textAlign="center" mb={10}>Perks That Matter</Typography>
                     <Grid container spacing={4}>
                         {[
                             { icon: <LaptopMacIcon />, title: "Remote Work", text: "Work from anywhere in the world." },
                             { icon: <SchoolIcon />, title: "Learning Budget", text: "We pay for your courses and books." },
                             { icon: <AutoAwesomeIcon />, title: "Annual Retreats", text: "We fly everyone out for a week of fun." },
                             { icon: <HealthAndSafetyIcon />, title: "Health & Wellness", text: "Comprehensive insurance and gym access." }
                         ].map((perk, i) => (
                             <Grid item xs={12} sm={6} md={3} key={i}>
                                 <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 4, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }, transition: '0.3s' }}>
                                     <Box sx={{ color: '#69F0AE', mb: 2, transform: 'scale(1.5)' }}>{perk.icon}</Box>
                                     <Typography variant="h6" fontWeight={700} gutterBottom>{perk.title}</Typography>
                                     <Typography variant="body2" sx={{ opacity: 0.7 }}>{perk.text}</Typography>
                                 </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* 🔹 HIRING PROCESS - DARK & SLEEK */}
            <Box sx={{ py: 15, bgcolor: '#0a0a0a', color: 'white', position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, #1DE9B6, transparent)' }} />
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                     <SectionHeader title="Your Journey to Us" subtitle="Hiring Process" center light />
                     <Grid container spacing={4} justifyContent="center" alignItems="center" sx={{ mt: 5 }}>
                         {processSteps.map((step, i) => (
                             <React.Fragment key={i}>
                                 <Grid item xs={6} md={2} sx={{ textAlign: 'center', position: 'relative' }}>
                                    <motion.div whileHover={{ scale: 1.1 }}>
                                        <Box sx={{ width: 90, height: 90, borderRadius: '50%', border: '2px solid rgba(29, 233, 182, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2, bgcolor: 'rgba(29, 233, 182, 0.05)', color: '#1DE9B6', boxShadow: '0 0 20px rgba(29, 233, 182, 0.1)' }}>
                                            {step.icon}
                                        </Box>
                                    </motion.div>
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'white' }}>{step.label}</Typography>
                                    <Typography variant="caption" sx={{ color: '#ccc' }}>{step.desc}</Typography>
                                 </Grid>
                                 {i < processSteps.length - 1 && (
                                     <Grid item xs={1} sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'center' }}>
                                         <Box sx={{ height: 2, background: 'linear-gradient(90deg, #1DE9B6, transparent)', width: '100%', opacity: 0.5 }} />
                                     </Grid>
                                 )}
                             </React.Fragment>
                         ))}
                     </Grid>
                </Container>
             </Box>

             {/* 🔹 LIFE AT GROWAITECH - VIBRANT GRID */}
             <Box sx={{ py: 15, bgcolor: '#fff' }}>
                 <Container maxWidth="lg">
                     <SectionHeader title="Life at GrowAITech" subtitle="Inside Look" center />
                     <Grid container spacing={3} sx={{ mt: 4 }}>
                         <Grid item xs={12} md={8}>
                             <motion.div whileHover={{ scale: 1.01 }}>
                                 <Box sx={{ 
                                     height: '450px', 
                                     borderRadius: 6, 
                                     backgroundImage: `url(${retreatImg})`,
                                     backgroundSize: 'cover',
                                     backgroundPosition: 'center',
                                     display: 'flex', 
                                     alignItems: 'flex-end', 
                                     p: 4, 
                                     color: 'white', 
                                     boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                                     position: 'relative',
                                     '::before': { content: '""', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: 6, background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)' }
                                     }}>
                                     <Box sx={{ position: 'relative', zIndex: 1 }}>
                                         <Typography variant="overline" sx={{ opacity: 0.8 }}>Team Events</Typography>
                                         <Typography variant="h4" fontWeight={800}>Annual Retreat in Bali</Typography>
                                     </Box>
                                 </Box>
                             </motion.div>
                         </Grid>
                         <Grid item xs={12} md={4}>
                             <Box sx={{ height: '450px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                                 <motion.div sx={{ flex: 1 }} whileHover={{ scale: 1.02 }}>
                                     <Box sx={{ 
                                         height: '210px', 
                                         borderRadius: 6, 
                                         backgroundImage: `url(${hackathonImg})`,
                                         backgroundSize: 'cover',
                                         backgroundPosition: 'center',
                                         display: 'flex', 
                                         alignItems: 'flex-end', 
                                         p: 3, 
                                         color: 'white', 
                                         boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                                         position: 'relative',
                                         '::before': { content: '""', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: 6, background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)' }
                                     }}>
                                         <Box sx={{ position: 'relative', zIndex: 1 }}>
                                             <Typography variant="h6" fontWeight={800}>Hackathons</Typography>
                                         </Box>
                                     </Box>
                                 </motion.div>
                                 <motion.div sx={{ flex: 1 }} whileHover={{ scale: 1.02 }}>
                                     <Box sx={{ 
                                         height: '216px', 
                                         borderRadius: 6, 
                                         backgroundImage: `url(${remoteImg})`,
                                         backgroundSize: 'cover',
                                         backgroundPosition: 'center',
                                         display: 'flex', 
                                         alignItems: 'flex-end', 
                                         p: 3, 
                                         color: 'white', 
                                         boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                                         position: 'relative',
                                         '::before': { content: '""', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: 6, background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)' }
                                     }}>
                                         <Box sx={{ position: 'relative', zIndex: 1 }}>
                                              <Typography variant="h6" fontWeight={800}>Remote Friendly</Typography>
                                         </Box>
                                     </Box>
                                 </motion.div>
                             </Box>
                         </Grid>
                     </Grid>
                 </Container>
             </Box>


            {/* 🔹 OPEN POSITIONS with COLORFUL TAGS */}
            <Box id="open-positions" sx={{ py: 15, bgcolor: '#FAFAFA' }}>
                <Container maxWidth="lg">
                    <SectionHeader title="Open Roles" subtitle="Join the Mission" center />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {jobs.map((job, index) => (
                            <motion.div key={index} whileHover={{ x: 10 }} transition={{ type: 'tween' }}>
                                <Paper elevation={0} sx={{ p: 4, borderRadius: 6, bgcolor: 'white', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, gap: 4, transition: '0.3s', boxShadow: '0 4px 20px rgba(0,0,0,0.02)', '&:hover': { boxShadow: '0 10px 40px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' }, borderLeft: `6px solid ${job.color}` }}>
                                    <Box sx={{ width: 70, height: 70, borderRadius: 3, bgcolor: `${job.color}15`, color: job.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        {job.icon}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ sm: 'center' }} mb={1}>
                                            <Typography variant="h5" fontWeight={800} sx={{ color: '#1a1a1a' }}>{job.title}</Typography>
                                            <Chip label={job.department} size="small" sx={{ bgcolor: job.color, color: 'white', fontWeight: 700, width: 'fit-content' }} />
                                            {job.title.includes('AI') && <Chip label="Hot" size="small" sx={{ bgcolor: 'black', color: 'white', fontWeight: 700 }} />}
                                        </Box>
                                        <Typography variant="body1" sx={{ color: '#333', mb: 2, maxWidth: '80%' }}>{job.desc}</Typography>
                                        <Box display="flex" gap={1} flexWrap="wrap">
                                            {['Remote', 'Full-Time'].map(tag => (
                                                <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ borderColor: '#e0e0e0', color: '#666', fontWeight: 500 }} />
                                            ))}
                                        </Box>
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                         <Button 
                                             variant="contained" 
                                             size="large" 
                                             href="https://docs.google.com/forms/d/e/1FAIpQLSe4iEq2OQAsWAxgoxufazlq9Ef8nF-jqY8_BKNkFBF8Q0TIsg/viewform?usp=sharing&ouid=100720090043935279908"
                                             target="_blank"
                                             rel="noopener noreferrer"
                                             sx={{ bgcolor: '#1a1a1a', color: 'white', textTransform: 'none', borderRadius: 50, px: 5, py: 1.5, fontWeight: 700, '&:hover': { bgcolor: job.color } }}
                                         >
                                             Apply Now
                                         </Button>
                                    </Box>
                                </Paper>
                            </motion.div>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* 🔹 FAQ SECTION */}
            <Box sx={{ py: 15, bgcolor: '#ffff' }}>
                <Container maxWidth="md">
                    <SectionHeader title="Frequently Asked Questions" subtitle="FAQ" center />
                    <Box display="flex" flexDirection="column" gap={3}>
                         {faqs.map((f, i) => (
                             <Paper key={i} elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid #f0f0f0', transition: '0.3s', '&:hover': { borderColor: '#ccc' } }}>
                                 <Typography variant="h6" fontWeight={700} gutterBottom color="#002613">{f.q}</Typography>
                                 <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>{f.a}</Typography>
                             </Paper>
                         ))}
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}

export default Careers;
